import type { ClimbRouteLogResponse, ClimbRouteOut } from "@/types/api";
import * as api from "@/api/routes";
import { getCurrentUserId } from "./currentUser";
import {
  blobToObjectUrl,
  deletePendingRoute,
  getPendingRoute,
  isLocalId,
  listPendingRoutesForSession,
  nextLocalId,
  putPendingRoute,
  revokeObjectUrl,
} from "./db";
import { OfflineEditBlockedError } from "./errors";
import { isNetworkError, isOnline } from "./network";
import { clearRouteSyncStatus, getRouteSyncStatus, setRouteSyncStatus } from "./statusCache";
import { refreshPendingCounts } from "./syncEngine";
import type { PendingRoute, RouteData } from "./types";
import { withoutNulls } from "./utils";

export { getRouteSyncStatus, clearRouteSyncStatus };

function toClimbRouteOut(pending: PendingRoute): ClimbRouteOut {
  setRouteSyncStatus(pending.id, pending.status);
  return {
    id: pending.id,
    session_id: pending.sessionId,
    grade: pending.data.grade,
    is_tall_wall: pending.data.is_tall_wall,
    route_name: pending.data.route_name ?? null,
    discipline: pending.data.discipline ?? null,
    send_type: pending.data.send_type,
    notes: pending.data.notes ?? null,
    photo_path: pending.photoBlob ? blobToObjectUrl(pending.id, pending.photoBlob) : null,
    video_path: null,
    video_status: null,
    base_points: 0,
    total_points: 0,
    climbed_at: pending.createdAt,
  };
}

// Server-computed fields (points, PR/badge/pyramid detection) can't be known
// offline. Defaulting them falsy means a PR/badge/pyramid completion earned
// offline simply won't trigger its celebration overlay, even after sync —
// an accepted gap, not worth a "replay missed celebrations" mechanism.
function toClimbRouteLogResponse(pending: PendingRoute): ClimbRouteLogResponse {
  return {
    ...toClimbRouteOut(pending),
    challenge_complete: false,
    challenge_just_completed: false,
    routes_climbed: 0,
    total_points_in_window: 0,
    points_goal: 0,
    is_new_pr: false,
    previous_best_grade: null,
    pyramid_complete: false,
    newly_earned_badges: [],
  };
}

export async function listPendingRoutesAsOutForSession(sessionId: number): Promise<ClimbRouteOut[]> {
  const routes = await listPendingRoutesForSession(sessionId);
  return routes.map(toClimbRouteOut);
}

export async function logRoute(sessionId: number, data: RouteData): Promise<ClimbRouteLogResponse> {
  // A still-local session was never sent to the server, so there's nothing
  // to POST a route against yet regardless of connectivity.
  if (!isLocalId(sessionId) && (await isOnline())) {
    try {
      return await api.logRoute(sessionId, withoutNulls(data));
    } catch (err) {
      if (!isNetworkError(err)) throw err;
    }
  }

  const userId = getCurrentUserId();
  if (userId == null) throw new Error("Cannot log a route while signed out.");

  const id = await nextLocalId();
  const pending: PendingRoute = {
    id,
    sessionId,
    createdByUserId: userId,
    status: "pending",
    createdAt: new Date().toISOString(),
    data,
  };
  await putPendingRoute(pending);
  void refreshPendingCounts();
  return toClimbRouteLogResponse(pending);
}

export async function updateRoute(
  sessionId: number,
  routeId: number,
  data: Partial<RouteData>
): Promise<ClimbRouteOut> {
  if (isLocalId(routeId)) {
    const pending = await getPendingRoute(routeId);
    if (!pending) throw new Error("Route not found.");
    // Editing a failed item is how the user fixes and re-queues it.
    const updated: PendingRoute = {
      ...pending,
      data: { ...pending.data, ...data },
      status: "pending",
      error: undefined,
    };
    await putPendingRoute(updated);
    void refreshPendingCounts();
    return toClimbRouteOut(updated);
  }
  if (!(await isOnline())) throw new OfflineEditBlockedError();
  return api.updateRoute(sessionId, routeId, data);
}

export async function deleteRoute(sessionId: number, routeId: number): Promise<void> {
  if (isLocalId(routeId)) {
    revokeObjectUrl(routeId);
    clearRouteSyncStatus(routeId);
    await deletePendingRoute(routeId);
    void refreshPendingCounts();
    return;
  }
  if (!(await isOnline())) throw new OfflineEditBlockedError();
  await api.deleteRoute(sessionId, routeId);
}

export async function uploadRoutePhoto(sessionId: number, routeId: number, blob: Blob): Promise<void> {
  if (isLocalId(routeId)) {
    const pending = await getPendingRoute(routeId);
    if (!pending) throw new Error("Route not found.");
    revokeObjectUrl(routeId);
    await putPendingRoute({ ...pending, photoBlob: blob });
    return;
  }
  if (!(await isOnline())) throw new OfflineEditBlockedError();
  await api.uploadRoutePhoto(sessionId, routeId, blob);
}

export async function deleteRoutePhoto(sessionId: number, routeId: number): Promise<ClimbRouteOut> {
  if (isLocalId(routeId)) {
    const pending = await getPendingRoute(routeId);
    if (!pending) throw new Error("Route not found.");
    revokeObjectUrl(routeId);
    const updated: PendingRoute = { ...pending, photoBlob: undefined };
    await putPendingRoute(updated);
    return toClimbRouteOut(updated);
  }
  if (!(await isOnline())) throw new OfflineEditBlockedError();
  return api.deleteRoutePhoto(sessionId, routeId);
}
